import React, { useMemo } from 'react';

import { useClassnames } from '@lumapps/classnames';
import { get as getConstants, get as getConfig } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { useDataAttributes } from '@lumapps/data-attributes';
import { ImageLightbox, useImageLightbox } from '@lumapps/lumx-images/components/ImageLightbox';
import { mdiArrowExpand, mdiLink, mdiOpenInNew } from '@lumapps/lumx/icons';
import {
    Badge,
    Button,
    Icon,
    IconButton,
    Mosaic,
    Text,
    Theme,
    Thumbnail,
    ThumbnailProps,
    useTheme,
} from '@lumapps/lumx/react';
import { makeSecuredMediaURLRelative } from '@lumapps/medias/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { ElementRender } from '@lumapps/wrex/types';

import { CLASSNAME, IMAGE_GROUP, IMAGE_WIDTHS } from '../../../constants';
import { ImageGroupElement } from '../../../types';

import './index.scss';

export interface EnhancedImageProps {
    imgRef?: React.Ref<HTMLImageElement>;
    isInEditionMode?: boolean;
}

const { isMobileAppWebView, mobileOS } = getConstants();

/**
 * Render image group as single image or mosaic images
 */
export const EnhancedImage: ElementRender<ImageGroupElement, HTMLDivElement, EnhancedImageProps> = (props) => {
    const { className, elementRef, element, children, isInEditionMode, imgRef, ...forwardedProps } = props;
    const theme = useTheme() || Theme.light;
    const { translateKey } = useTranslate();
    const { element: elementClass, block: blockClass } = useClassnames(CLASSNAME);
    const isMobileApp = isMobileAppWebView || !!mobileOS;
    const isWebview = getConfig().applicationId === AppId.webview;
    const { get } = useDataAttributes(CLASSNAME);

    const { images = [], title, isLoading } = element ?? {};

    const isFullWidth = element?.width === IMAGE_WIDTHS.fullWidth;
    const isHalfWidth = element?.width === IMAGE_WIDTHS.halfWidth;

    const thumbnails = useMemo(
        () =>
            images.map(({ src = '', alt = '', tempSrc, height, width }): ThumbnailProps => {
                const image = tempSrc || makeSecuredMediaURLRelative(src) || '';
                const imgProps = { height, width };

                return { image, title, alt, imgProps };
            }),
        [images, title],
    );

    const width = isFullWidth ? undefined : thumbnails[0]?.imgProps?.width;

    const { getTriggerProps, imageLightboxProps } = useImageLightbox(thumbnails);

    const getContextTriggerProps = (activeImageIndex: number) => {
        if (isInEditionMode) {
            return undefined;
        }

        // If image have a link, return linkProps
        if (images[activeImageIndex].link) {
            return {
                linkProps: {
                    href: images[0].link,
                    target: '_blank',
                },
            };
        }

        // Otherwise, return open lightbox props
        return getTriggerProps({ activeImageIndex });
    };

    return (
        <figure
            {...forwardedProps}
            ref={elementRef}
            className={blockClass(
                {
                    'is-loading': !!isLoading,
                    multiple: thumbnails.length > 1,
                    'full-width': isFullWidth,
                    'half-width': isHalfWidth,
                },
                className,
            )}
        >
            {thumbnails.length === 1 && (
                <div className={elementClass('thumbnail-wrapper')}>
                    <Thumbnail
                        className={elementClass('thumbnail')}
                        imgRef={imgRef}
                        {...thumbnails[0]}
                        {...getContextTriggerProps(0)}
                        contentEditable={false}
                        imgProps={{
                            ...thumbnails[0].imgProps,
                            // Inline width style to reverse space while loading
                            style: { width },
                            draggable: isInEditionMode ? 'false' : undefined,
                        }}
                    />
                    {images[0].link && (
                        <>
                            {/* Link badge - short version */}
                            <Badge
                                color="dark"
                                className={elementClass('link-badge-short', {
                                    'force-full-link': isMobileApp,
                                })}
                                contentEditable={false}
                                tabIndex={-1}
                            >
                                <Icon icon={mdiLink} color="light" />
                            </Badge>
                            {/* Link badge - full version */}
                            <Button
                                className={elementClass('link-badge-full', {
                                    'force-full-link': isMobileApp,
                                    'is-mobile': isMobileApp,
                                })}
                                color="dark"
                                leftIcon={mdiOpenInNew}
                                size={isMobileApp ? 'm' : 's'}
                                href={images[0].link}
                                target="_blank"
                                contentEditable={false}
                                tabIndex={-1}
                                {...get({ element: 'link' })}
                            >
                                <Text as="span" typography="caption" truncate color="light" colorVariant="N">
                                    {images[0].link}
                                </Text>
                            </Button>
                            {/* Button to open the lightbox */}
                            {!isInEditionMode && !isWebview && (
                                <div className={elementClass('open-lightbox-button')}>
                                    <IconButton
                                        color="dark"
                                        label={translateKey(GLOBAL.OPEN_IMAGE_IN_FULLSCREEN)}
                                        emphasis="low"
                                        icon={mdiArrowExpand}
                                        size={isMobileApp ? 'm' : 's'}
                                        {...getTriggerProps({ activeImageIndex: 0 })}
                                        {...get({ element: 'link' })}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            {thumbnails.length > 1 && (
                <Mosaic
                    className={elementClass('mosaic')}
                    style={{ width: '100%' }}
                    thumbnails={thumbnails.map((thumbnail, index) => ({
                        ...thumbnail,
                        ...getContextTriggerProps(index),
                    }))}
                    contentEditable={false}
                    theme={theme}
                />
            )}
            {children}
            {!isInEditionMode && title && (
                <figcaption className={elementClass('caption')} style={{ width }}>
                    {title}
                </figcaption>
            )}
            <ImageLightbox {...imageLightboxProps} />
        </figure>
    );
};
EnhancedImage.displayName = IMAGE_GROUP;
